const HISTORY_LIMIT = 10;
const STORAGE_PREFIX = "afrimed:institution-home:recent:v1:";
export const INSTITUTION_HOME_HISTORY_CHANGED = "afrimed:institution-home-history-changed";

function text(value) {
  return String(value || "").trim();
}

function storageKey(userId) {
  const user = text(userId);
  return user ? `${STORAGE_PREFIX}${user}` : "";
}

function emptyHistory() {
  return { institutions: [], members: [] };
}

function validInstitutionEntry(value) {
  const parentInstitutionId = text(value?.parentInstitutionId);
  const facilityId = text(value?.facilityId);
  const viewedAt = text(value?.viewedAt);
  return parentInstitutionId && facilityId && viewedAt
    ? { parentInstitutionId, facilityId, viewedAt }
    : null;
}

function validMemberEntry(value) {
  const parentInstitutionId = text(value?.parentInstitutionId);
  const facilityId = text(value?.facilityId);
  const membershipId = text(value?.membershipId);
  const userId = text(value?.userId);
  const viewedAt = text(value?.viewedAt);
  return parentInstitutionId && (membershipId || userId) && viewedAt
    ? { parentInstitutionId, facilityId: facilityId || null, membershipId: membershipId || null, userId: userId || null, viewedAt }
    : null;
}

function recentFirst(entries, key) {
  const seen = new Set();
  return entries
    .filter(Boolean)
    .sort((left, right) => String(right.viewedAt).localeCompare(String(left.viewedAt)))
    .filter((entry) => {
      const identity = key(entry);
      if (!identity || seen.has(identity)) return false;
      seen.add(identity);
      return true;
    })
    .slice(0, HISTORY_LIMIT);
}

function normaliseHistory(raw) {
  const value = raw && typeof raw === "object" ? raw : {};
  return {
    institutions: recentFirst(
      (Array.isArray(value.institutions) ? value.institutions : []).map(validInstitutionEntry),
      (entry) => `${entry.parentInstitutionId}:${entry.facilityId}`,
    ),
    members: recentFirst(
      (Array.isArray(value.members) ? value.members : []).map(validMemberEntry),
      (entry) => `${entry.parentInstitutionId}:${entry.facilityId || "parent"}:${entry.membershipId || entry.userId}`,
    ),
  };
}

export function readInstitutionHomeHistory(userId) {
  const key = storageKey(userId);
  if (!key || typeof window === "undefined") return emptyHistory();
  try {
    return normaliseHistory(JSON.parse(window.localStorage.getItem(key) || "{}"));
  } catch {
    return emptyHistory();
  }
}

export function writeInstitutionHomeHistory(userId, history) {
  const key = storageKey(userId);
  const next = normaliseHistory(history);
  if (!key || typeof window === "undefined") return next;
  try {
    window.localStorage.setItem(key, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent(INSTITUTION_HOME_HISTORY_CHANGED, { detail: { userId: text(userId) } }));
  } catch {
    // A private browser mode can reject local storage. Home remains an empty, safe surface.
  }
  return next;
}

export function recordInstitutionHomeFacilityView(userId, { parentInstitutionId, facilityId } = {}) {
  const entry = validInstitutionEntry({ parentInstitutionId, facilityId, viewedAt: new Date().toISOString() });
  if (!entry) return readInstitutionHomeHistory(userId);
  const current = readInstitutionHomeHistory(userId);
  return writeInstitutionHomeHistory(userId, { ...current, institutions: [entry, ...current.institutions] });
}

export function recordInstitutionHomeMemberView(userId, { parentInstitutionId, facilityId, membershipId, userId: memberUserId } = {}) {
  const entry = validMemberEntry({ parentInstitutionId, facilityId, membershipId, userId: memberUserId, viewedAt: new Date().toISOString() });
  if (!entry) return readInstitutionHomeHistory(userId);
  const current = readInstitutionHomeHistory(userId);
  return writeInstitutionHomeHistory(userId, { ...current, members: [entry, ...current.members] });
}

export function rehydrateInstitutionHomeHistory(history, { parentInstitutionId, facilities = [], members = [] } = {}) {
  const parentId = text(parentInstitutionId);
  const facilityById = new Map((Array.isArray(facilities) ? facilities : []).map((facility) => [text(facility?.id), facility]).filter(([id]) => id));
  const memberRows = Array.isArray(members) ? members : [];
  const memberFor = (entry) => memberRows.find((member) => (
    // Parent-Member snapshots legitimately omit the facility through which an
    // authorised Member was opened. Match the governed membership identity
    // first; facility metadata must never erase a valid parent relationship.
    ((entry.membershipId && text(member?.membershipId || member?.id) === entry.membershipId)
      || (entry.userId && text(member?.userId || member?.user_id) === entry.userId))
  ));
  const safeHistory = normaliseHistory(history);
  const institutions = safeHistory.institutions
    .filter((entry) => entry.parentInstitutionId === parentId)
    .map((entry) => ({ entry, facility: facilityById.get(entry.facilityId) }))
    .filter(({ facility }) => Boolean(facility));
  const resolvedMembers = safeHistory.members
    .filter((entry) => entry.parentInstitutionId === parentId)
    .map((entry) => ({ entry, member: memberFor(entry) }))
    .filter(({ member }) => Boolean(member));
  // Rehydration proves only the active parent Institution's records. Keep the
  // same user's opaque history for other parent Institutions intact so a safe
  // visit in one authorised workspace cannot erase another workspace's
  // separate history.
  const pruned = normaliseHistory({
    institutions: [
      ...safeHistory.institutions.filter((entry) => entry.parentInstitutionId !== parentId),
      ...institutions.map(({ entry }) => entry),
    ],
    members: [
      ...safeHistory.members.filter((entry) => entry.parentInstitutionId !== parentId),
      ...resolvedMembers.map(({ entry }) => entry),
    ],
  });
  return { institutions, members: resolvedMembers, pruned };
}

export { HISTORY_LIMIT };
